import React, {useEffect, useRef} from 'react';
import {Box, CircularProgress, Stack, Typography} from '@mui/material';
import useScopedCommentsMessages from './useScopedCommentsMessages';
import {useSession} from '../../../../firebase/UserProvider';
import {formatTime} from '../../../../utils/formatTimestamp';
import OwnMessageBubble from '../../../../components/OwnMessageBubble';

const BASE = process.env.REACT_APP_AWP_API_URL || 'https://awp.minsky.ai';

/**
 * Message list for comments-backed chats.
 * Defaults to OneOnOne expert DMs; pass elementName (e.g. 'whiteboard') for other scopes.
 */
const ExpertsChatMessages = ({chatId, elementName = 'OneOnOne', fullHeight, onReady}) => {
    const {messages, isLoading, addOptimisticMessage} = useScopedCommentsMessages(elementName, chatId);

    useEffect(() => {
        if (onReady) onReady(addOptimisticMessage);
    }, [onReady, addOptimisticMessage]);

    const bottomRef = useRef(null);
    const {user} = useSession();

    useEffect(() => {
        if (!fullHeight || !messages.length) return;
        bottomRef.current?.scrollIntoView({behavior: 'smooth', block: 'end'});
    }, [messages, fullHeight]);

    if (!chatId) return (
        <Stack flex={1} alignItems={'center'} justifyContent={'center'}>
            <Typography variant="body2" color="text.secondary">No chat selected</Typography>
        </Stack>
    );

    if (isLoading) return (
        <Stack flex={1} alignItems={'center'} justifyContent={'center'}>
            <CircularProgress size={20}/>
        </Stack>
    );

    return (
        <Stack
            flex={fullHeight ? 1 : 'none'}
            height={fullHeight ? '100%' : 'auto'}
            overflow={'auto'}
            px={2}
            py={1}
            spacing={1}
        >
            {messages.length === 0 && (
                <Stack flex={1} alignItems={'center'} justifyContent={'center'} py={4}>
                    <Typography variant="body2" color="text.secondary">
                        No messages yet. Say hello!
                    </Typography>
                </Stack>
            )}

            {messages.map((msg) => {
                const isMine = user && msg.userId === user.uid;

                if (isMine) {
                    return (
                        <OwnMessageBubble key={msg.messageId} timestamp={msg.publishedDatetime}>
                            {msg.imageUrl && (
                                <Box
                                    component="img"
                                    src={`${BASE}${msg.imageUrl}`}
                                    alt="shared image"
                                    sx={{
                                        display: 'block',
                                        maxWidth: 280,
                                        maxHeight: 320,
                                        width: '100%',
                                        borderRadius: 1,
                                        mb: msg.content ? 0.75 : 0,
                                        cursor: 'pointer',
                                    }}
                                    onClick={() => window.open(`${BASE}${msg.imageUrl}`, '_blank')}
                                />
                            )}
                            {msg.content && (
                                <Typography variant="body1" sx={{whiteSpace: 'pre-wrap'}}>
                                    {msg.content}
                                </Typography>
                            )}
                        </OwnMessageBubble>
                    );
                }

                // Others — flat two-row layout
                return (
                    <Stack key={msg.messageId} spacing={0.25}>
                        <Typography variant="caption" color="text.secondary" fontWeight={500}>
                            {msg.userDisplayName}
                        </Typography>
                        {msg.imageUrl && (
                            <Box
                                component="img"
                                src={`${BASE}${msg.imageUrl}`}
                                alt="shared image"
                                sx={{
                                    display: 'block',
                                    maxWidth: 280,
                                    maxHeight: 320,
                                    width: '100%',
                                    borderRadius: 1,
                                    mb: msg.content ? 0.75 : 0,
                                    cursor: 'pointer',
                                }}
                                onClick={() => window.open(`${BASE}${msg.imageUrl}`, '_blank')}
                            />
                        )}
                        {msg.content && (
                            <Typography variant="body1" sx={{whiteSpace: 'pre-wrap'}}>
                                {msg.content}
                            </Typography>
                        )}
                        <Typography variant="caption" color="text.disabled">
                            {formatTime(msg.publishedDatetime)}
                        </Typography>
                    </Stack>
                );
            })}

            <div ref={bottomRef}/>
        </Stack>
    );
};

export default ExpertsChatMessages;
